import React, { Suspense } from "react"
import { Analytics } from "@vercel/analytics/react"
import { Header, Footer } from "./LandingPage"
import { Waves, Users, Rocket, Mail } from "lucide-react"
import GradientBackground from './sections/GradientBackground'
import SEO from './common/SEO'
const AccordionSection = React.lazy(() => import('./sections/AccordionSection'))

function AccordionSkeleton() {
  return (
    <div className="space-y-4 w-full animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-gray-100 h-16 rounded-lg" />
      ))}
    </div>
  )
}

const AboutPage: React.FC = () => {
  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO path="/about" />
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="container flex flex-col items-center justify-center space-y-4 pt-32 text-center relative z-20">
          <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl">
            Making Ocean Technology<br />
            <span className="text-[#1F80FF]">Work for Every Diver</span>
          </h1>
          <p className="max-w-[600px] text-xl text-muted-foreground">
            Currents helps divers focus on what matters most - exploring and protecting our oceans. 
            We handle the tech, so you can dive deeper into your passion.
          </p>
        </section>

        {/* Mission Section */}
        <section className="container py-20 relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1200px] mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-[#1F80FF]/10 rounded-xl flex items-center justify-center mb-4">
                <Waves className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Ocean First</h3>
              <p className="text-muted-foreground">
                We're passionate about ocean conservation and believe that better technology 
                can help divers make a positive impact on marine ecosystems.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-[#1F80FF]/10 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Vibrant Community</h3>
              <p className="text-muted-foreground">
                We're building more than an app - we're creating a global community of 
                divers who share knowledge, experiences, and passion for the ocean.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-[#1F80FF]/10 rounded-xl flex items-center justify-center mb-4">
                <Rocket className="w-6 h-6 text-[#1F80FF]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Tech Advanced</h3>
              <p className="text-muted-foreground">
                We leverage cutting-edge technology to make diving safer, more accessible, 
                and more enjoyable for everyone.
              </p>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="container py-20 bg-gray-50 rounded-3xl relative z-20">
          <div className="max-w-[800px] mx-auto text-center space-y-16">
            <div className="space-y-6">
              <p className="text-xl text-muted-foreground leading-relaxed">
                We're on a mission to make diving technology work for <span className="text-[#1F80FF]">everyone</span>, not just tech experts.
                Every feature we build starts with a real diver's need - whether that's a dive instructor 
                managing multiple computers or a new diver logging their first <span className="text-[#1F80FF]">shore dive</span>.
              </p>
            </div>

            <div className="space-y-6">
              <p className="text-xl text-muted-foreground leading-relaxed">
                We believe diving should be about <span className="text-[#1F80FF]">discovery</span> and <span className="text-[#1F80FF]">connection</span> - with the ocean, with other divers, 
                with marine life. Technology should enhance these experiences, not complicate them.
              </p>
            </div>

            <div className="space-y-6">
              <p className="text-xl text-muted-foreground leading-relaxed">
                We build tools that don't just store your dive data, but help you <span className="text-[#1F80FF]">grow</span> as a diver. 
                Tools that connect you with a global <span className="text-[#1F80FF]">community</span> of ocean enthusiasts, sharing knowledge 
                and protecting the waters we love to explore.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container py-20 relative z-20">
          <div className="max-w-[800px] mx-auto space-y-8">
            <div className="text-center space-y-4">
              <div className="inline-block rounded-full bg-[#1F80FF]/10 px-3 py-1 text-sm text-[#1F80FF]">
                FAQ
              </div>
              <h2 className="text-3xl font-bold">Common Questions</h2>
              <p className="text-lg text-muted-foreground">
                Everything you need to know about Currents
              </p>
            </div>

            <Suspense fallback={<AccordionSkeleton />}>
              <AccordionSection />
            </Suspense>
          </div>
        </section>
      
        {/* Contact Section */}
        <section className="container py-20 relative z-20">
          <div className="max-w-[600px] mx-auto text-center space-y-8">
            <div className="inline-flex items-center space-x-2 bg-[#1F80FF]/10 rounded-full px-4 py-2">
              <Mail className="w-5 h-5 text-[#1F80FF]" />
              <span className="text-[#1F80FF] font-medium">Get in Touch</span>
            </div>
            <h2 className="text-3xl font-bold">Let's Make Waves Together</h2>
            <p className="text-lg text-muted-foreground">
              Have questions or suggestions? We'd love to hear from you.
            </p>
            <a 
              href="mailto:hello@deepsealabs.co"
              className="inline-block text-xl text-[#1F80FF] hover:text-[#1F80FF]/80 font-medium"
            >
              hello@deepsealabs.co
            </a>
          </div>
        </section>
      </main>
      <GradientBackground />
      <Footer />
      <Analytics />
    </div>
  )
}

export default AboutPage 